import api from '../base';
import {
  CreatedPartner,
  NextPartnerCode,
  Partner,
  PartnerCodeAvailability,
  PartnerCreateRequest,
  PartnerInfo,
  PartnerInfoRequest,
  PartnerWorkflowStatusRequest,
  Webhook,
} from './partnerApiTypes';
import {
  mockApiToken,
  mockCreatedPartner,
  mockNextPartnerCode,
  mockPartnerCodeAvailability,
  mockPartnerInfo,
  mockPartners,
  mockPartnerWebhook,
} from './partnerMocks';

const BASE_PATH = 'management/partners';

export async function getPartnerInfo(): Promise<PartnerInfo> {
  return api.get<PartnerInfo>(`${BASE_PATH}/info`).then(response => response.data);
}

api.mock.onGet(`${BASE_PATH}/info`).reply(200, mockPartnerInfo);

export async function savePartnerInfo(info: PartnerInfoRequest): Promise<string> {
  return api.post<string>(`${BASE_PATH}/info`, info).then(response => response.data);
}

api.mock.onPost(`${BASE_PATH}/info`).reply(202);

export async function savePartnerWorkflowStatus(
  request: PartnerWorkflowStatusRequest,
): Promise<void> {
  await api.post(`${BASE_PATH}/workflow-status`, request);
}

api.mock.onPost(`${BASE_PATH}/workflow-status`).reply(200);

export async function getPartnersWebhook(): Promise<Webhook> {
  return api.get(`${BASE_PATH}/webhook`).then(response => response.data);
}

api.mock.onGet(`${BASE_PATH}/webhook`).reply(200, mockPartnerWebhook);

export async function setPartnerWebhookUrl(webhook: Webhook) {
  return api.patch(`${BASE_PATH}/webhook`, webhook);
}

api.mock.onPatch(`${BASE_PATH}/webhook`).reply(204);

export async function generateApiToken() {
  return api.post(`${BASE_PATH}/token/generate`).then(response => response.data);
}

api.mock.onPost(`${BASE_PATH}/token/generate`).reply(200, mockApiToken);

export async function saveApiToken(apiToken: string) {
  return api.post(`${BASE_PATH}/token`, apiToken, {
    headers: {
      'Content-Type': 'text/plain',
    },
  });
}

api.mock.onPost(`${BASE_PATH}/token`).reply(204);

export async function isApiTokenGenerated(): Promise<boolean> {
  return api.get<boolean>(`${BASE_PATH}/token`).then(response => response.data);
}

export async function getPartners(): Promise<Partner[]> {
  return api.get<Partner[]>(BASE_PATH).then(response => response.data);
}

api.mock.onGet(BASE_PATH).reply(200, mockPartners);

export async function getNextPartnerCode(signal?: AbortSignal): Promise<NextPartnerCode> {
  const response = await api.get<NextPartnerCode>(`${BASE_PATH}/code/next`, { signal });

  return response.data;
}

api.mock.onGet(`${BASE_PATH}/code/next`).reply(200, mockNextPartnerCode);

export async function getPartnerCodeAvailability(
  code: string,
  signal?: AbortSignal,
): Promise<PartnerCodeAvailability> {
  const response = await api.get<PartnerCodeAvailability>(`${BASE_PATH}/code/availability`, {
    params: { code },
    signal,
  });

  return response.data;
}

api.mock.onGet(`${BASE_PATH}/code/availability`).reply(config => [
  200, mockPartnerCodeAvailability(String(config.params?.code ?? '')),
]);

export async function createPartner(
  request: PartnerCreateRequest,
  signal?: AbortSignal,
): Promise<CreatedPartner> {
  const response = await api.post<CreatedPartner>(BASE_PATH, request, { signal });

  return response.data;
}

api.mock.onPost(BASE_PATH).reply(config => [
  201, mockCreatedPartner(JSON.parse(config.data)),
]);
